import {
	AddressRes,
	BankAccountRes,
	BulkUploadPersonLegalsDto,
	BulkUploadPersonNaturalDto,
	CreateOneSchemaPersonSessionDto,
	CreateOneSchemaSessionSuccessRes,
	CreatePersonAddressDto,
	CreateSimplePersonNaturalDto,
	DataUserTct,
	EmploymentEmployerRes,
	EmploymentRecordRes,
	GetPersonByIdRes,
	GetPersonTaxInformationRes,
	GetPersonForCapRes,
	IsPersonEmailInUseRes,
	IsInvestmentWorkflowExistsRes,
	PersonEmploymentRes,
	PersonLegalRes,
	PersonNameWithIdRes,
	PersonNaturalRes,
	PersonTagRes,
	UpdateNaturalPersonDto,
	UpdateNaturalPersonEmailDto,
	UpdatePersonAddressDto,
	UpdatePersonDto,
	UpsertPersonEmploymentDto,
} from '@dealstack/shared/lib/generated';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';

export const getLegalEntities = async (
	endpoint: string,
	args: { dealId: string; onlyIssuers?: boolean; pivOfPersonId?: string }
) => {
	const { dealId, onlyIssuers, pivOfPersonId } = args;
	return makeRequest<ApiV1.Response<GetPersonByIdRes[]>>({
		endpoint: formatUnicorn(endpoint, { dealId }),
		method: HTTP_METHOD.GET,
		queryString: { onlyIssuers, pivOfPersonId },
	}).then((response) => response.payload ?? []);
};

export const getPersonsForCap = async (endpoint: string, args: { dealId: string }) => {
	const { dealId } = args;

	return makeRequest<ApiV1.Response<GetPersonForCapRes[]>>({
		endpoint: formatUnicorn(endpoint, { dealId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getPersonTagsForDeal = async (endpoint: string, args: { dealId: string }) => {
	const { dealId } = args;

	return makeRequest<ApiV1.Response<PersonTagRes[]>>({
		endpoint: formatUnicorn(endpoint, { dealId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getPersonById = async (
	endpoint: string,
	args: { personId: string },
	signal?: AbortSignal
) => {
	const { personId } = args;
	return makeRequest<ApiV1.Response<GetPersonByIdRes>>(
		{
			endpoint: formatUnicorn(endpoint, { personId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const createNaturalPerson = async (params: {
	dealId: string;
	createArgs: CreateSimplePersonNaturalDto;
}): Promise<PersonNaturalRes> => {
	return await makeRequest<ApiV1.Response<PersonNaturalRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_DEAL_DEALID_PERSON_CREATE_NATURAL_POST, {
			dealId: params.dealId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(params.createArgs),
	}).then((response) => response.payload as PersonNaturalRes);
};

export const createPersonLegal = async (params: {
	dealId: string;
	createArgs: { name: string };
}): Promise<PersonLegalRes> => {
	return await makeRequest<ApiV1.Response<PersonLegalRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_DEAL_DEALID_PERSON_CREATE_LEGAL_POST, {
			dealId: params.dealId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(params.createArgs),
	}).then((response) => response.payload as PersonLegalRes);
};

export const updatePersonLegal = async (params: {
	personId: string;
	updateArgs: UpdatePersonDto;
}): Promise<PersonLegalRes> => {
	return await makeRequest<ApiV1.Response<PersonLegalRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_LEGAL_PUT, {
			personId: params.personId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(params.updateArgs),
	}).then((response) => response.payload as PersonLegalRes);
};

export const updatePersonNatural = async (params: {
	personId: string;
	updateArgs: UpdateNaturalPersonDto;
}): Promise<PersonNaturalRes> => {
	return await makeRequest<ApiV1.Response<PersonNaturalRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_NATURAL_PUT, {
			personId: params.personId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(params.updateArgs),
	}).then((response) => response.payload as PersonNaturalRes);
};

export const getPersonBankAccounts = async (personId: string): Promise<BankAccountRes[]> => {
	return await makeRequest<ApiV1.Response<BankAccountRes[]>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_BANKACCOUNT_GET, { personId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload ?? []);
};

export const getPersonTaxInformation = async (
	personId: string
): Promise<GetPersonTaxInformationRes[]> => {
	return await makeRequest<ApiV1.Response<GetPersonTaxInformationRes[]>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_TAX_GET, { personId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload ?? []);
};

export const getPersonEmploymentRecords = async (
	personId: string
): Promise<EmploymentRecordRes[]> => {
	return await makeRequest<ApiV1.Response<EmploymentRecordRes[]>>({
		endpoint: formatUnicorn(API_PATHS.PERSONS_PERSONID_EMPLOYMENT_RECORDS_GET, { personId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload ?? []);
};

export const getEmploymentEmployers = async (): Promise<EmploymentEmployerRes[]> => {
	return await makeRequest<ApiV1.Response<EmploymentEmployerRes[]>>({
		endpoint: API_PATHS.EMPLOYMENT_EMPLOYERS_GET,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload as EmploymentEmployerRes[]);
};

export const getPersonEmployment = async (personId: string): Promise<PersonEmploymentRes[]> => {
	return await makeRequest<ApiV1.Response<PersonEmploymentRes[]>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_EMPLOYMENT_GET, { personId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload as PersonEmploymentRes[]);
};

export const upsertPersonEmployment = async (params: {
	personId: string;
	updateArgs: UpsertPersonEmploymentDto;
}): Promise<PersonEmploymentRes> => {
	return await makeRequest<ApiV1.Response<PersonEmploymentRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_EMPLOYMENT_PUT, {
			personId: params.personId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(params.updateArgs),
	}).then((response) => response.payload as PersonEmploymentRes);
};

export const updatePersonNaturalEmail = async (params: {
	personId: string;
	updateArgs: UpdateNaturalPersonEmailDto;
}): Promise<PersonNaturalRes> => {
	return await makeRequest<ApiV1.Response<PersonNaturalRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_NATURAL_EMAIL_PUT, {
			personId: params.personId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(params.updateArgs),
	}).then((response) => response.payload as PersonNaturalRes);
};

export const deletePerson = async (args: {
	deletePersonDto: string[];
	force: boolean;
}): Promise<string[]> => {
	const endpoint = formatUnicorn(API_PATHS.PERSON_DELETE_ENTITIES_POST, {});
	const { deletePersonDto, force } = args;
	return makeRequest<string[]>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(deletePersonDto),
		queryString: { force },
	});
};

export const createPersonAddress = async (params: {
	personId: string;
	createArgs: CreatePersonAddressDto;
}): Promise<AddressRes> => {
	return await makeRequest<ApiV1.Response<AddressRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_CREATE_ADDRESS_POST, {
			personId: params.personId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(params.createArgs),
	}).then((response) => response.payload as AddressRes);
};

export const updatePersonAddress = async (params: {
	personId: string;
	updateArgs: UpdatePersonAddressDto;
}): Promise<AddressRes> => {
	return await makeRequest<ApiV1.Response<AddressRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_UPDATE_ADDRESS_PUT, {
			personId: params.personId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(params.updateArgs),
	}).then((response) => response.payload as AddressRes);
};

export const deletePersonAddress = async (params: {
	personAddressId: string;
}): Promise<AddressRes> => {
	return await makeRequest<ApiV1.Response<AddressRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONADDRESSID_DELETE_ADDRESS_DELETE, {
			personAddressId: params.personAddressId,
		}),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload as AddressRes);
};

export const bulkUploadLegalPersons = async ({
	data,
	dealId,
}: {
	data: BulkUploadPersonLegalsDto;
	dealId: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_DEAL_DEALID_CREATE_LEGAL_BULK_POST, { dealId }),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const bulkUploadNaturalPersons = async ({
	data,
	dealId,
}: {
	data: BulkUploadPersonNaturalDto;
	dealId: string;
}): Promise<ApiV1.Response<void>> => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_DEAL_DEALID_CREATE_NATURAL_BULK_POST, {
			dealId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const isThereInvestmentWorkflowPerson = async (personsToCheck: string[]) => {
	return makeRequest<ApiV1.Response<IsInvestmentWorkflowExistsRes>>({
		endpoint: API_PATHS.PERSON_IS_THERE_INVESTMENTWORKFLOW_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(personsToCheck),
	}).then((response) => response.payload);
};

export const getDealAdmins = async (
	endpoint: string,
	args: { dealId: string },
	signal?: AbortSignal
) => {
	const { dealId } = args;

	return makeRequest<ApiV1.Response<DataUserTct[]>>(
		{
			endpoint: formatUnicorn(endpoint, { dealId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const getNaturalPersonsByPivId = async (pivPersonId: string) => {
	return makeRequest<ApiV1.Response<PersonNameWithIdRes[]>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PIVPERSONID_NATURAL_PERSONS_BY_PIV_GET, {
			pivPersonId,
		}),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const isPersonEmailInUse = async (personId: string) =>
	makeRequest<ApiV1.Response<IsPersonEmailInUseRes>>({
		endpoint: formatUnicorn(API_PATHS.PERSON_PERSONID_IS_USER_INVITED_GET, {
			personId,
		}),
		method: HTTP_METHOD.GET,
	}).then((response) => !!response.payload?.isEmailInUse);

export const createOneSchemaPersonsSession = async (args: CreateOneSchemaPersonSessionDto) => {
	return await makeRequest<ApiV1.Response<CreateOneSchemaSessionSuccessRes>>({
		endpoint: API_PATHS.ONE_SCHEMA_PERSONS_CREATE_ONE_SCHEMA_SESSION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};
